import { loadBookingAgentState } from '../booking/storage';

const PROFILE_FIELDS = [
  'dateOfBirth',
  'phone',
  'address',
  'emergencyContactName',
  'emergencyContactPhone',
  'conditions',
  'medications',
  'allergies',
  'symptoms',
  'preferredLanguage',
  'mobilityStatus',
  'carePreferences',
  'consentStatus'
];

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function daysBetween(fromDate, toDate = new Date()) {
  const from = new Date(fromDate);
  if (Number.isNaN(from.getTime())) return 0;
  const diffMs = toDate.getTime() - from.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

export function computeHealthProfileCompleteness(profile = {}) {
  const completed = PROFILE_FIELDS.filter((field) => {
    const value = profile[field];
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'boolean') return true;
    return Boolean(String(value || '').trim());
  }).length;

  return {
    completed,
    total: PROFILE_FIELDS.length,
    percentage: Math.round((completed / PROFILE_FIELDS.length) * 100)
  };
}

function buildBookingRecommendation(language = 'fr') {
  const bookingState = loadBookingAgentState();
  if (!bookingState?.step || bookingState.step === 'triage') return null;

  return {
    id: 'booking-resume',
    kind: 'journey',
    priority: 'medium',
    title: language === 'fr' ? 'Reprendre votre parcours de réservation' : 'Resume your booking journey',
    body: language === 'fr'
      ? 'Votre demande de soins est déjà entamée. Reprenez là où vous vous êtes arrêté.'
      : 'Your care request is already in progress. Pick up where you left off.',
    reason: language === 'fr' ? 'Votre progression a ete enregistree automatiquement.' : 'Your progress was saved automatically so you can continue without starting over.',
    actionLabel: language === 'fr' ? 'Continuer la réservation' : 'Continue booking',
    actionType: 'booking',
    bookingPrefill: bookingState,
    source: 'booking-agent'
  };
}

function buildObservationRecommendations({ subject, observations = [], language = 'fr' }) {
  const recommendations = [];
  const conditions = (subject?.healthProfile?.conditions || []).map(normalizeText);

  observations.forEach((observation) => {
    if (observation.status === 'dismissed') return;

    const symptom = normalizeText(observation.symptom || observation.issue || observation.category);
    const noteType = normalizeText(observation.noteType);
    const severity = normalizeText(observation.severity);
    const reviewDays = daysBetween(observation.observedAt || observation.createdAt);

    if (
      (conditions.includes('diabetes') || conditions.includes('diabète')) &&
      (symptom.includes('foot') || symptom.includes('pied')) &&
      (symptom.includes('cut') || symptom.includes('plaie') || symptom.includes('wound'))
    ) {
      recommendations.push({
        id: `obs-${observation.id}-foot-care`,
        kind: 'care-follow-up',
        priority: severity === 'high' ? 'high' : 'medium',
        title: language === 'fr' ? 'Suggérer un suivi en soins des pieds' : 'Suggest a foot-care follow-up',
        body: language === 'fr'
          ? 'Une observation indique une plaie au pied chez une personne diabétique. Un suivi ciblé peut aider à éviter une aggravation.'
          : 'An observation notes a foot wound for someone with diabetes. A focused follow-up can help reduce the risk of complications.',
        reason: language === 'fr'
          ? 'Une observation recente mentionne une plaie au pied chez une personne atteinte de diabete.'
          : 'A recent observation mentions a foot wound for someone living with diabetes.',
        actionLabel: language === 'fr' ? 'Trouver un professionnel' : 'Find care',
        actionType: 'booking',
        bookingPrefill: {
          freeText: language === 'fr'
            ? 'Plaie au pied chez une personne diabétique, besoin de suivi en soins des pieds'
            : 'Foot wound for a person with diabetes, looking for foot care follow-up',
          selectedRoute: 'other'
        },
        source: observation.id,
        sourceType: 'observation',
        safeNote: language === 'fr'
          ? 'Cette recommandation n’est pas un diagnostic. Si l’état s’aggrave rapidement, contactez un professionnel de santé sans attendre.'
          : 'This suggestion is not a diagnosis. If symptoms worsen quickly, contact a clinician promptly.'
      });
    }

    if (
      (symptom.includes('cough') || symptom.includes('toux')) &&
      reviewDays >= 7
    ) {
      recommendations.push({
        id: `obs-${observation.id}-pediatrics`,
        kind: 'symptom-follow-up',
        priority: reviewDays >= 14 ? 'high' : 'medium',
        title: language === 'fr' ? 'Vérifier si un suivi pédiatrique est indiqué' : 'Check whether a pediatric follow-up is appropriate',
        body: language === 'fr'
          ? 'La toux est notée depuis plusieurs jours. Une évaluation clinique peut être utile si elle persiste.'
          : 'The cough has been logged for several days. A clinical review may help if it is continuing.',
        reason: language === 'fr'
          ? `La toux est suivie depuis ${reviewDays} jours.`
          : `The cough has been tracked for ${reviewDays} days.`,
        actionLabel: language === 'fr' ? 'Planifier un suivi' : 'Plan follow-up',
        actionType: 'booking',
        bookingPrefill: {
          freeText: language === 'fr'
            ? 'Toux persistante chez un enfant, besoin de consultation pédiatrique'
            : 'Persistent cough in a child, looking for a pediatric consultation',
          selectedRoute: 'other'
        },
        source: observation.id,
        sourceType: 'observation'
      });
    }

    if (
      noteType.includes('milestone') &&
      (severity === 'medium' || severity === 'high' || normalizeText(observation.outcomeStatus).includes('not_met'))
    ) {
      recommendations.push({
        id: `obs-${observation.id}-milestone`,
        kind: 'milestone',
        priority: 'medium',
        title: language === 'fr' ? 'Réévaluer la progression attendue' : 'Reassess the expected progress',
        body: language === 'fr'
          ? 'Le jalon de soins semble non atteint. Une revue avec le prestataire peut clarifier la meilleure prochaine étape.'
          : 'The care milestone appears unmet. A provider review can help confirm the best next step.',
        reason: language === 'fr'
          ? 'Le resultat attendu n a pas encore ete atteint selon la note la plus recente.'
          : 'The most recent note suggests the expected improvement has not been reached yet.',
        actionLabel: language === 'fr' ? 'Voir le dossier de soins' : 'Open care record',
        actionType: 'record',
        source: observation.id,
        sourceType: 'observation'
      });
    }
  });

  return recommendations;
}

export function deriveExperience(user, managedSubjects = [], activeSubject = null) {
  if (!user) return 'guest';
  if (user.user_type === 'professional') return 'provider';
  const delegateMatch = managedSubjects.some((subject) => subject.id !== user.user_id);
  if (delegateMatch && activeSubject?.id !== user.user_id) return 'delegate';
  return 'patient';
}

export function buildRecommendations({
  user,
  activeSubject,
  managedSubjects = [],
  observations = [],
  appointments = [],
  feedback = [],
  language = 'fr'
}) {
  if (!user) {
    return [];
  }

  const results = [];
  const completeness = computeHealthProfileCompleteness(activeSubject?.healthProfile || {});
  const experience = deriveExperience(user, managedSubjects, activeSubject);

  if (activeSubject && completeness.percentage < 85) {
    results.push({
      id: `profile-${activeSubject.id}`,
      kind: 'profile',
      priority: completeness.percentage < 50 ? 'high' : 'medium',
      title: language === 'fr' ? 'Compléter le profil de santé' : 'Complete the health profile',
      body: language === 'fr'
        ? 'Ajoutez les renseignements médicaux, préférences et contacts clés pour améliorer le suivi et les suggestions.'
        : 'Add the key medical, preference, and emergency details to improve follow-up and recommendations.',
      reason: language === 'fr'
        ? `Votre profil de sante est complete a ${completeness.percentage}%.`
        : `Your health profile is only ${completeness.percentage}% complete.`,
      actionLabel: language === 'fr' ? 'Mettre à jour le profil' : 'Update profile',
      actionType: 'profile',
      source: 'health-profile'
    });
  }

  const bookingRecommendation = buildBookingRecommendation(language);
  if (bookingRecommendation) {
    results.push(bookingRecommendation);
  }

  results.push(...buildObservationRecommendations({
    subject: activeSubject,
    observations: observations.filter((entry) => entry.subjectId === activeSubject?.id),
    language
  }));

  if (experience === 'patient' && managedSubjects.length === 1) {
    results.push({
      id: 'delegate-setup',
      kind: 'delegation',
      priority: 'low',
      title: language === 'fr' ? 'Autoriser un proche de confiance' : 'Authorize a trusted family member',
      body: language === 'fr'
        ? 'Ajoutez un proche autorisé pour l’aider à gérer les rendez-vous, suivis et rappels avec consentement explicite.'
        : 'Add an authorized loved one to help manage appointments, follow-ups, and reminders with explicit consent.',
      reason: language === 'fr' ? 'Aucun acces delegue actif n est encore configure.' : 'No trusted contact has been added yet.',
      actionLabel: language === 'fr' ? 'Gérer les consentements' : 'Manage consent',
      actionType: 'consent',
      source: 'consent'
    });
  }

  const recentCompletedAppointment = appointments.find((appointment) => {
    const endValue = appointment.end_utc || appointment.end_time || appointment.end;
    if (!endValue) return false;
    return new Date(endValue) < new Date();
  });

  if (recentCompletedAppointment && !feedback.some((entry) => entry.linkedId === recentCompletedAppointment.appointment_id || entry.linkedId === recentCompletedAppointment.id)) {
    results.push({
      id: `feedback-${recentCompletedAppointment.appointment_id || recentCompletedAppointment.id}`,
      kind: 'feedback',
      priority: 'low',
      title: language === 'fr' ? 'Partager votre retour après le service' : 'Share feedback after care',
      body: language === 'fr'
        ? 'Votre retour aide à améliorer la coordination et la qualité de l’expérience.'
        : 'Your feedback helps improve coordination and service quality.',
      reason: language === 'fr' ? 'Un service recent a ete repere dans votre parcours.' : 'A recent service was completed in your care journey.',
      actionLabel: language === 'fr' ? 'Donner mon avis' : 'Give feedback',
      actionType: 'feedback',
      source: 'feedback'
    });
  }

  if (user.user_type === 'professional') {
    const providerObservations = observations.filter((entry) => entry.authorId === user.user_id || entry.providerId === user.user_id);
    if (!providerObservations.length) {
      results.push({
        id: 'provider-observation',
        kind: 'provider-task',
        priority: 'medium',
        title: language === 'fr' ? 'Ajouter une observation structurée' : 'Add a structured observation',
        body: language === 'fr'
          ? 'Documentez les symptômes, notes intelligentes et points de suivi pour alimenter les relances futures.'
          : 'Document symptoms, smart notes, and follow-up checkpoints to power future follow-ups.',
        reason: language === 'fr' ? 'Aucune observation structuree recente n a encore ete enregistree.' : 'No recent structured observations have been added yet.',
        actionLabel: language === 'fr' ? 'Ouvrir le module de notes' : 'Open notes workflow',
        actionType: 'provider-observation',
        source: 'care-observations'
      });
    }
  }

  return results.sort((a, b) => {
    const scores = { high: 0, medium: 1, low: 2 };
    return (scores[a.priority] ?? 3) - (scores[b.priority] ?? 3);
  });
}
